import { Component, computed, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TypographyComponent } from '@purolator/ui-kit-angular';
import type { PickupAvailabilitySlot } from '../../../../core/api/pickup/models/pickup.model';
import { TranslatePipe, TranslationService } from '../../../../core/i18n';
import {
  FloatingSelect,
  type FloatingSelectOption,
} from '../../../../shared/components/floating-select/floating-select';
import { AlertToast } from '../../../../shared/components/alert-toast/alert-toast';
import type { AddressFormValue } from '../../../ship-from-ship-to/models/address-form.model';
import {
  buildPickupLocationOptions,
  isSelectablePickupLocationCode,
} from './mappers/build-pickup-location-options';
import {
  buildWindowOptionsForSlot,
  formatCurrentLocalHhmm,
  formatLocalIsoDate,
  formatPickupDateLabel,
  type PickupDateOption,
  type PickupDisplayLocale,
  type ShipFromLocalClockOptions,
} from './mappers/map-pickup-availability';
import { PickupAddressContactSectionComponent } from './pickup-address-contact/pickup-address-contact-section.component';
import type { PickupAddressDisplay } from './pickup-address-contact/pickup-address-contact-section.component';
import { PickupDatesSectionComponent } from './pickup-dates/pickup-dates-section.component';
import type { InductionMethod, PickupDropOffState } from './pickup-drop-off.state';
import { NearestDropOffLocationComponent } from './nearest-drop-off-location/nearest-drop-off-location.component';
import { PickupWindowSectionComponent } from './pickup-window/pickup-window-section.component';
import type { PickupWindowChange } from './pickup-window/pickup-window-section.component';

/**
 * Pickup/Drop-off shell section for the Service step.
 * Matches Figma “Drop Off or Pickup” (ESO Lite): title + three-option button group on
 * desktop/medium, floating-select dropdown on mobile, intro copy, nearest drop-off card
 * when Drop-off is selected, and schedule-pickup content when pickup is selected.
 */
@Component({
  selector: 'app-pickup-drop-off',
  imports: [
    TypographyComponent,
    TranslatePipe,
    FloatingSelect,
    FormsModule,
    AlertToast,
    NearestDropOffLocationComponent,
    PickupDatesSectionComponent,
    PickupWindowSectionComponent,
    PickupAddressContactSectionComponent,
  ],
  templateUrl: './pickup-drop-off.component.html',
  styleUrl: './pickup-drop-off.component.scss',
})
export class PickupDropOffComponent {
  private readonly translation = inject(TranslationService);

  readonly state = input.required<PickupDropOffState>();
  /** Ship From address used for the read-only pickup contact summary. */
  readonly shipFrom = input<AddressFormValue | null>(null);
  /**
   * When false, Schedule a pickup remains visible but is not selectable (config disable).
   * Defaults to true so hosts that omit the input keep pickup available.
   */
  readonly pickupSchedulingEnabled = input(true);
  readonly availabilityLoading = input(false);
  readonly availabilityError = input<string | null>(null);
  /** Soft availability notice shown with date radios (e.g. same-day cutoff). */
  readonly availabilityNotice = input<string | null>(null);
  /**
   * When true, show the not-supported alert with Drop-off link and hide scheduling fields.
   */
  readonly pickupNotSupportedAtAddress = input(false);
  /** Customer message for invalid / unavailable pickup windows. */
  readonly windowNotice = input<string | null>(null);
  readonly locationsLoading = input(false);
  readonly locationsError = input<string | null>(null);
  /** Nearest drop-off locator search in flight. */
  readonly locatorLoading = input(false);
  /** Customer-facing locator unavailable message. */
  readonly locatorError = input<string | null>(null);
  /**
   * When true after Save & Continue, show drop-off field validation if no nearest address
   * is selected (create requires `dropOffInformation`).
   */
  readonly showValidationErrors = input(false);

  /** Emits the newly selected induction method; the page owns state updates. */
  readonly methodChange = output<InductionMethod>();
  /** Emits when the user selects a pickup date radio. */
  readonly dateChange = output<string>();
  /** Emits when the ready/close window selects change. */
  readonly windowChange = output<PickupWindowChange>();
  /** Emits when the pickup-from location code changes. */
  readonly locationCodeChange = output<string | null>();
  /** Emits when courier instructions change. */
  readonly courierInstructionsChange = output<string>();

  /**
   * Mobile dropdown options for the three induction methods (Figma 390px print 4).
   * Order: Drop-off → Create label only → Schedule a pickup.
   * Recomputes when locale revision changes so EN/FR labels stay in sync.
   * Marks Schedule a pickup disabled when config turns pickup off or validate says
   * pickup is not supported at the Ship From address.
   */
  protected readonly methodOptions = computed<FloatingSelectOption[]>(() => {
    this.translation.revision();
    const pickupDisabled = this.pickupMethodDisabled();
    return [
      {
        value: 'drop-off',
        label: this.translation.translate('createShipment.serviceStep.pickupDropOff.dropOffLabel'),
      },
      {
        value: 'label-only',
        label: this.translation.translate(
          'createShipment.serviceStep.pickupDropOff.labelOnlyLabel',
        ),
      },
      {
        value: 'pickup',
        label: this.translation.translate('createShipment.serviceStep.pickupDropOff.pickupLabel'),
        disabled: pickupDisabled,
      },
    ];
  });

  /**
   * True when Schedule a pickup must not be selectable (config off or validate not-supported).
   */
  protected readonly pickupMethodDisabled = computed(
    () => !this.pickupSchedulingEnabled() || this.pickupNotSupportedAtAddress(),
  );

  /**
   * Date radio options derived from validate `pickUps`, labeled for the active locale.
   */
  protected readonly dateOptions = computed<PickupDateOption[]>(() => {
    this.translation.revision();
    const locale = this.displayLocale();
    const clock = this.shipFromLocalClock();
    const today = formatLocalIsoDate(new Date(), clock);
    const slots = this.state().availability ?? [];

    return slots.slice(0, 2).map((slot) => ({
      date: slot.date,
      label: formatPickupDateLabel(
        slot.date,
        today,
        locale,
        this.translation.translate('createShipment.serviceStep.pickupDropOff.dateTodayLabel'),
      ),
      isToday: slot.date === today,
    }));
  });

  /**
   * From/to time options for the selected day's slot using Salesforce min duration.
   */
  protected readonly windowOptions = computed(() => {
    this.translation.revision();
    const state = this.state();
    const slot = this.findSelectedSlot(state.availability, state.selectedDate);
    if (!slot) {
      return { fromOptions: [], toOptions: [] };
    }

    const clock = this.shipFromLocalClock();
    const todayIso = formatLocalIsoDate(new Date(), clock);
    const currentLocalHhmm = formatCurrentLocalHhmm(new Date(), clock);
    const selectedFrom =
      state.windowFrom ?? (slot.date === todayIso ? currentLocalHhmm : slot.latestPickupStartTime);

    return buildWindowOptionsForSlot(slot, selectedFrom, {
      todayIso,
      currentLocalHhmm,
      locale: this.displayLocale(),
    });
  });

  /**
   * Pickup-from location select options from the API map, sorted A–Z (no Please Select).
   */
  protected readonly locationOptions = computed<FloatingSelectOption[]>(() => {
    this.translation.revision();
    const map = this.state().pickupFromMap;
    if (!map) {
      return [];
    }

    return buildPickupLocationOptions(map);
  });

  /**
   * Bound Pickup Location value — empty when no valid code is chosen yet.
   */
  protected readonly locationSelectValue = computed(() => {
    const code = this.state().locationCode;
    return isSelectablePickupLocationCode(code, this.state().pickupFromMap) ? code! : null;
  });

  /**
   * Read-only address/contact lines for the pickup panel, mapped from Ship From.
   */
  protected readonly addressDisplay = computed<PickupAddressDisplay | null>(() => {
    const shipFrom = this.shipFrom();
    if (!shipFrom) {
      return null;
    }

    return mapShipFromToAddressDisplay(shipFrom);
  });

  /** Ship From postal for the nearest drop-off card heading and Home Locator links. */
  protected readonly shipFromPostal = computed(() => {
    const postal = this.shipFrom()?.postalCode?.trim();
    return postal || null;
  });

  /**
   * Emits the selected induction method from the desktop button group or mobile select.
   * Ignores Schedule a pickup when config disabled it or validate marked the address unsupported.
   */
  protected onMethodChange(value: InductionMethod | null): void {
    if (value !== 'drop-off' && value !== 'label-only' && value !== 'pickup') {
      return;
    }

    if (value === 'pickup' && this.pickupMethodDisabled()) {
      return;
    }

    this.methodChange.emit(value);
  }

  /**
   * Emits the selected Pickup Location code, or null when cleared.
   */
  protected onLocationSelectChange(value: string | null): void {
    this.locationCodeChange.emit(value || null);
  }

  private displayLocale(): PickupDisplayLocale {
    return this.translation.locale() === 'fr' ? 'fr' : 'en';
  }

  /**
   * Builds Ship From clock options so From/To option lists use Ship From local time.
   */
  private shipFromLocalClock(): ShipFromLocalClockOptions {
    return { provinceCode: this.shipFrom()?.province ?? null };
  }

  private findSelectedSlot(
    availability: PickupAvailabilitySlot[] | null,
    selectedDate: string | null,
  ): PickupAvailabilitySlot | null {
    if (!availability || !selectedDate) {
      return null;
    }

    return availability.find((slot) => slot.date === selectedDate) ?? null;
  }
}

/**
 * Maps Ship From form values into the read-only pickup address summary lines.
 * Empty optional fields become null so the template can omit empty labels.
 */
export function mapShipFromToAddressDisplay(shipFrom: AddressFormValue): PickupAddressDisplay {
  const street = [shipFrom.streetAddress.trim(), shipFrom.suiteAptUnit.trim()]
    .filter(Boolean)
    .join(' ');
  const city = shipFrom.city.trim();
  const province = (shipFrom.province ?? '').trim();
  const postal = shipFrom.postalCode.trim();
  const cityProvincePostal = [city, [province, postal].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  const streetLine = [street, cityProvincePostal].filter(Boolean).join(', ') || null;

  let phone: string | null = shipFrom.phoneNumber.trim() || null;
  if (phone && shipFrom.phoneExtension.trim()) {
    phone = `${phone} x ${shipFrom.phoneExtension.trim()}`;
  }

  return {
    streetLine,
    name: shipFrom.companyOrFullName.trim() || null,
    phone,
    buzzer: shipFrom.entryBuzzerCode.trim() || null,
  };
}
